import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { dateLocale, formatDateTime } from "@/lib/dateTime";
import { TimeInput } from "@/components/ui/time-input";
import type { WaTemplate } from "@/types/whatsapp";

type VarState = { mode: "field" | "text"; field: string; text: string; fallback: string };
type FollowUp = { templateId: string; vars: VarState[]; days: string };
const MAX_FOLLOW_UPS = 3;
const newVars = (tpl?: WaTemplate): VarState[] =>
  Array.from({ length: (tpl?.header_variables.length || 0) + (tpl?.body_variables.length || 0) }, (_, i) => ({ mode: i === 0 ? "field" : "text", field: i === 0 ? "first_name" : "", text: "", fallback: i === 0 ? "there" : "" }) as VarState);
const varPayload = (vars: VarState[]) => vars.map((v) => (v.mode === "field" ? { field: v.field, fallback: v.fallback.trim() || undefined } : { text: v.text.trim() }));
const varsFilled = (vars: VarState[], n: number) => vars.length === n && vars.every((v) => (v.mode === "field" ? !!v.field : !!v.text.trim()));
type Preview = {
  total: number;
  will_send: number;
  skipped: { count: number; by_reason: Record<string, number> };
  days: number;
  first_send_at: string | null;
  last_send_at: string | null;
  daily_cap: number;
  sample: { lead: string; text: string } | null;
  samples: Array<{ step: number; delay_minutes: number; lead: string; text: string }>;
  follow_ups: number;
};

const selectClass = "h-9 w-full rounded-md border border-input bg-card px-2.5 text-[13px] outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30";
const label = "text-[12px] font-medium text-foreground/80";

/** What each blank ({{1}}, {{2}}…) of a template is filled with: a lead field (with a fallback) or fixed text */
function VariableRows({ names, vars, fieldOptions, prefix = "", onChange }: { names: string[]; vars: VarState[]; fieldOptions: Array<[string, string]>; prefix?: string; onChange: (i: number, patch: Partial<VarState>) => void }) {
  return (
    <>
      {names.map((n, i) => (
        <div key={`${n}-${i}`} className="grid gap-2 sm:grid-cols-[110px_1fr_1fr] sm:items-center">
          <span className="font-mono text-[12.5px]">{`{{${n}}}`} is</span>
          <SearchableSelect aria-label={`${prefix}What {{${n}}} is filled with`} value={vars[i]?.mode === "text" ? "__text" : vars[i]?.field || ""} onChange={(e) => (e.target.value === "__text" ? onChange(i, { mode: "text" }) : onChange(i, { mode: "field", field: e.target.value }))} className={selectClass}>
            <option value="">Choose…</option>
            {fieldOptions.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            <option value="__text">Fixed text…</option>
          </SearchableSelect>
          {vars[i]?.mode === "text" ? (
            <Input aria-label={`${prefix}Text for {{${n}}}`} placeholder="Text for every lead" value={vars[i]?.text || ""} maxLength={200} onChange={(e) => onChange(i, { text: e.target.value })} />
          ) : (
            <Input aria-label={`${prefix}If empty, use for {{${n}}}`} placeholder="If the lead has none, say…" value={vars[i]?.fallback || ""} maxLength={60} onChange={(e) => onChange(i, { fallback: e.target.value })} />
          )}
        </div>
      ))}
    </>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** The leads ticked in the list */
  selectedIds: string[];
  /** All leads matching the list's current filters, and the filters themselves (sent as they are) */
  matching: { total: number; filters: Record<string, any> };
  onCreated: (id: string) => void;
}

/** Leads → WhatsApp campaign: pick an approved template, say what each blank is filled with, check who would get it, then start */
export function CampaignDialog({ open, onOpenChange, selectedIds, matching, onCreated }: Props) {
  const { error, success } = useToast();
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [settings, setSettings] = useState<{ connected: boolean; daily_cap: number; fields: Record<string, string> }>({ connected: true, daily_cap: 250, fields: {} });
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState("");
  const [scope, setScope] = useState<"selected" | "matching">("selected");
  const [templateId, setTemplateId] = useState("");
  const [vars, setVars] = useState<VarState[]>([]);
  const [when, setWhen] = useState<"now" | "later">("now");
  const [sendAt, setSendAt] = useState("");
  const [dailyCap, setDailyCap] = useState("250");
  const [windowStart, setWindowStart] = useState("09:00");
  const [windowEnd, setWindowEnd] = useState("19:00");
  const [skipRecent, setSkipRecent] = useState("30");
  const [consent, setConsent] = useState(false);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [taskOnNoReply, setTaskOnNoReply] = useState(true);
  const [endWaitDays, setEndWaitDays] = useState("3");

  const [preview, setPreview] = useState<Preview | null>(null);
  const [checking, setChecking] = useState(false);
  const [problem, setProblem] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get("/api/WhatsApp/Templates").then((r) => unwrapArray<WaTemplate>(r)).catch(() => [] as WaTemplate[]),
      api.get("/api/WhatsAppCampaign/Settings").then((r) => unwrapObject<any>(r)).catch(() => null),
    ]).then(([t, s]) => {
      // Campaigns need text-only templates (no image, video or document header) for now
      setTemplates(t.filter((x) => !x.header_format || x.header_format === "TEXT"));
      if (s) {
        setSettings({ connected: !!s.connected, daily_cap: s.daily_cap, fields: s.fields || {} });
        setDailyCap(String(s.daily_cap || 250));
      }
    }).finally(() => setLoading(false));
    setName(`Cold leads · ${new Date().toLocaleDateString(dateLocale(), { day: "numeric", month: "short" })}`);
    setScope(selectedIds.length ? "selected" : "matching");
    setTemplateId(""); setVars([]); setFollowUps([]); setTaskOnNoReply(true); setEndWaitDays("3"); setWhen("now"); setSendAt(""); setConsent(false); setPreview(null); setProblem("");
    setWindowStart("09:00"); setWindowEnd("19:00"); setSkipRecent("30");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = templates.find((x) => x.id === templateId);
  const names = useMemo(() => (t ? [...t.header_variables, ...t.body_variables] : []), [t]);
  const choose = (id: string) => {
    setTemplateId(id);
    setVars(newVars(templates.find((x) => x.id === id)));
    setPreview(null);
  };
  const setVar = (i: number, patch: Partial<VarState>) => { setVars((v) => v.map((x, j) => (j === i ? { ...x, ...patch } : x))); setPreview(null); };
  const tplOf = (id: string) => templates.find((x) => x.id === id);
  const namesOf = (id: string) => { const x = tplOf(id); return x ? [...x.header_variables, ...x.body_variables] : []; };
  const setFollow = (i: number, patch: Partial<FollowUp>) => { setFollowUps((f) => f.map((x, j) => (j === i ? { ...x, ...patch } : x))); setPreview(null); };
  const chooseFollow = (i: number, id: string) => setFollow(i, { templateId: id, vars: newVars(tplOf(id)) });
  const setFollowVar = (i: number, k: number, patch: Partial<VarState>) => { setFollowUps((f) => f.map((x, j) => (j === i ? { ...x, vars: x.vars.map((v, m) => (m === k ? { ...v, ...patch } : v)) } : x))); setPreview(null); };

  const body = () => ({
    name: name.trim(),
    template_id: templateId,
    variables: varPayload(vars),
    follow_ups: followUps.length ? followUps.map((f) => ({ template_id: f.templateId, variables: varPayload(f.vars), delay_minutes: Math.round((Number(f.days) || 0) * 1440) })) : undefined,
    task_on_no_reply: taskOnNoReply,
    end_wait_minutes: Math.max(1, Math.round((Number(endWaitDays) || 3) * 1440)),
    audience: scope === "selected" ? { lead_ids: selectedIds } : { filters: matching.filters },
    send_at: when === "later" && sendAt ? new Date(sendAt).toISOString() : undefined,
    daily_cap: Number(dailyCap) || undefined,
    window_start: windowStart,
    window_end: windowEnd,
    skip_recent_days: Number(skipRecent),
  });
  const filled = varsFilled(vars, names.length);
  const followsOk = followUps.every((f) => !!f.templateId && varsFilled(f.vars, namesOf(f.templateId).length) && Number(f.days) >= 1);
  const ready = !!name.trim() && !!t && filled && followsOk && (when === "now" || !!sendAt);
  const audienceCount = scope === "selected" ? selectedIds.length : matching.total;

  const check = async () => {
    setChecking(true); setProblem(""); setPreview(null);
    try {
      setPreview(unwrapObject<Preview>(await api.post("/api/WhatsAppCampaign/Preview", body())));
    } catch (e: any) {
      setProblem(e?.message || "Couldn't check the campaign");
    } finally {
      setChecking(false);
    }
  };

  const start = async () => {
    setSubmitting(true);
    try {
      const c = unwrapObject<{ id: string }>(await api.post("/api/WhatsAppCampaign/Create", { ...body(), consent: true }));
      success("Campaign started", "Messages go out within the daily limit. Follow it on the Campaigns tab.");
      onOpenChange(false);
      onCreated(c.id);
    } catch (e: any) {
      error("Couldn't start the campaign", e?.message || "Please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldOptions = Object.entries(settings.fields);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>WhatsApp campaign</DialogTitle>
          <DialogDescription>Send one approved template to a group of leads, spread over the days the daily limit needs. Replies reach each lead's owner in the WhatsApp inbox.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>
        ) : !settings.connected ? (
          <p className="py-6 text-sm text-muted-foreground">WhatsApp isn't connected yet. An admin sets it up in Settings → WhatsApp.</p>
        ) : (
          <div className="space-y-5">
            <section className="space-y-3">
              <h3 className="text-[13px] font-semibold">1 · Who</h3>
              <div className="space-y-1.5">
                <Label htmlFor="cmp-name" className={label}>Campaign name</Label>
                <Input id="cmp-name" value={name} maxLength={120} onChange={(e) => { setName(e.target.value); setPreview(null); }} />
              </div>
              <div className="space-y-1.5 text-[13px]">
                <label className="flex items-center gap-2"><input type="radio" name="cmp-scope" checked={scope === "selected"} disabled={!selectedIds.length} onChange={() => { setScope("selected"); setPreview(null); }} />The {selectedIds.length} lead{selectedIds.length === 1 ? "" : "s"} I ticked</label>
                <label className="flex items-center gap-2"><input type="radio" name="cmp-scope" checked={scope === "matching"} onChange={() => { setScope("matching"); setPreview(null); }} />All {matching.total} lead{matching.total === 1 ? "" : "s"} matching the filters on the list{matching.total > 5000 ? " (the first 5,000)" : ""}</label>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-[13px] font-semibold">2 · Message</h3>
              {templates.length === 0 ? (
                <p className="text-sm text-muted-foreground">No usable template yet. Campaigns need an approved, text-only template. Create it in your WhatsApp business account, then an admin clicks "Sync templates" in Settings → WhatsApp.</p>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="cmp-template" className={label}>Template (approved by WhatsApp)</Label>
                    <SearchableSelect id="cmp-template" value={templateId} onChange={(e) => choose(e.target.value)} className={selectClass}>
                      <option value="">Choose a template</option>
                      {templates.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.language}) · {x.category}</option>)}
                    </SearchableSelect>
                  </div>
                  {t && (
                    <div className="rounded-md border border-border bg-muted/30 p-3 text-[13px] whitespace-pre-wrap">{[t.header_text, t.body_text, t.footer_text].filter(Boolean).join("\n\n")}</div>
                  )}
                  {t && <VariableRows names={names} vars={vars} fieldOptions={fieldOptions} onChange={setVar} />}
                </>
              )}
            </section>

            {t && (
              <section className="space-y-3">
                <h3 className="text-[13px] font-semibold">Follow-ups <span className="font-normal text-muted-foreground">(optional)</span></h3>
                <p className="text-xs text-muted-foreground">Another message to the leads who have <strong>not replied</strong>. Nobody who replied, sent STOP, was converted, or was messaged by a colleague gets it.</p>
                {followUps.map((f, i) => (
                  <div key={i} className="space-y-2 rounded-md border border-border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-[13px] font-medium">Follow-up {i + 1}</span>
                      <div className="flex items-center gap-2 text-[13px]">
                        <span>if no reply after</span>
                        <Input aria-label={`Follow-up ${i + 1}: days to wait`} className="h-8 w-16" inputMode="decimal" value={f.days} onChange={(e) => setFollow(i, { days: e.target.value.replace(/[^\d.]/g, "") })} />
                        <span>day{Number(f.days) === 1 ? "" : "s"}</span>
                        <Button type="button" variant="ghost" size="sm" aria-label={`Remove follow-up ${i + 1}`} onClick={() => { setFollowUps((x) => x.filter((_, j) => j !== i)); setPreview(null); }}>✕</Button>
                      </div>
                    </div>
                    <SearchableSelect aria-label={`Follow-up ${i + 1}: template`} value={f.templateId} onChange={(e) => chooseFollow(i, e.target.value)} className={selectClass}>
                      <option value="">Choose a template</option>
                      {templates.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.language}) · {x.category}</option>)}
                    </SearchableSelect>
                    {f.templateId && (
                      <>
                        <div className="rounded-md border border-border bg-muted/30 p-3 text-[13px] whitespace-pre-wrap">{[tplOf(f.templateId)?.header_text, tplOf(f.templateId)?.body_text, tplOf(f.templateId)?.footer_text].filter(Boolean).join("\n\n")}</div>
                        <VariableRows names={namesOf(f.templateId)} vars={f.vars} fieldOptions={fieldOptions} prefix={`Follow-up ${i + 1}: `} onChange={(k, patch) => setFollowVar(i, k, patch)} />
                      </>
                    )}
                  </div>
                ))}
                {followUps.length < MAX_FOLLOW_UPS && (
                  <Button type="button" variant="outline" size="sm" onClick={() => { setFollowUps((x) => [...x, { templateId: "", vars: [], days: "2" }]); setPreview(null); }}>+ Add a follow-up</Button>
                )}
                <label className="flex flex-wrap items-center gap-2 text-[13px]">
                  <input type="checkbox" checked={taskOnNoReply} onChange={(e) => { setTaskOnNoReply(e.target.checked); setPreview(null); }} />
                  <span>If there is still no reply, give the lead's owner a call task after</span>
                  <Input aria-label="Days before the call task" className="h-8 w-16" inputMode="decimal" value={endWaitDays} onChange={(e) => { setEndWaitDays(e.target.value.replace(/[^\d.]/g, "")); setPreview(null); }} />
                  <span>days</span>
                </label>
              </section>
            )}

            <section className="space-y-3">
              <h3 className="text-[13px] font-semibold">3 · When</h3>
              <div className="flex flex-wrap items-center gap-4 text-[13px]">
                <label className="flex items-center gap-2"><input type="radio" name="cmp-when" checked={when === "now"} onChange={() => { setWhen("now"); setPreview(null); }} />Start now</label>
                <label className="flex items-center gap-2"><input type="radio" name="cmp-when" checked={when === "later"} onChange={() => { setWhen("later"); setPreview(null); }} />Start later</label>
                {when === "later" && <Input type="datetime-local" aria-label="Start time" className="w-[210px]" value={sendAt} onChange={(e) => { setSendAt(e.target.value); setPreview(null); }} />}
              </div>
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="space-y-1.5"><Label htmlFor="cmp-cap" className={label}>At most per day</Label><Input id="cmp-cap" inputMode="numeric" value={dailyCap} onChange={(e) => { setDailyCap(e.target.value.replace(/\D/g, "")); setPreview(null); }} /></div>
                <div className="space-y-1.5"><span className={`block ${label}`}>Send from</span><TimeInput id="cmp-from" label="Send from" value={windowStart} onChange={(v) => { setWindowStart(v); setPreview(null); }} /></div>
                <div className="space-y-1.5"><span className={`block ${label}`}>Until</span><TimeInput id="cmp-to" label="Until" value={windowEnd} onChange={(v) => { setWindowEnd(v); setPreview(null); }} /></div>
                <div className="space-y-1.5"><Label htmlFor="cmp-recent" className={label}>Skip leads reached by a campaign in</Label>
                  <SearchableSelect id="cmp-recent" value={skipRecent} onChange={(e) => { setSkipRecent(e.target.value); setPreview(null); }} className={selectClass}>
                    <option value="30">the last 30 days</option><option value="7">the last 7 days</option><option value="0">Don't skip</option>
                  </SearchableSelect>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">WhatsApp limits how many new people your number may message per day (it starts at 250 and grows with good quality). Keep "At most per day" within your limit.</p>
            </section>

            <section className="space-y-3">
              <h3 className="text-[13px] font-semibold">4 · Check and start</h3>
              <Button type="button" variant="outline" disabled={!ready || checking} onClick={check}>
                {checking ? <><Loader2 className="mr-1.5 size-4 animate-spin" />Checking…</> : `Check the ${audienceCount} lead${audienceCount === 1 ? "" : "s"}`}
              </Button>
              {problem && <p className="text-sm text-destructive" role="alert">{problem}</p>}
              {preview && (
                <div className="space-y-2 rounded-md border border-border p-3 text-[13px]">
                  <p><strong>{preview.will_send}</strong> of {preview.total} lead{preview.total === 1 ? "" : "s"} will get this message{preview.skipped.count ? `; ${preview.skipped.count} skipped` : ""}.</p>
                  {Object.entries(preview.skipped.by_reason).map(([r, n]) => <p key={r} className="text-muted-foreground">• {n} — {r}</p>)}
                  {preview.will_send > 0 && <p className="text-muted-foreground">It takes {preview.days} day{preview.days === 1 ? "" : "s"}: first message {preview.first_send_at ? formatDateTime(preview.first_send_at) : "—"}, last {preview.last_send_at ? formatDateTime(preview.last_send_at) : "—"}.</p>}
                  {(preview.samples?.length ? preview.samples : preview.sample ? [{ step: 0, delay_minutes: 0, ...preview.sample }] : []).map((m) => (
                    <div key={m.step}>
                      <p className="mb-1 text-xs text-muted-foreground">{m.step === 0 ? `As ${m.lead} will see it:` : `Follow-up ${m.step}, ${Math.round(m.delay_minutes / 1440 * 10) / 10} day${m.delay_minutes === 1440 ? "" : "s"} later if there is no reply:`}</p>
                      <div className="whitespace-pre-wrap rounded-lg bg-success-soft px-3 py-2">{m.text}</div>
                    </div>
                  ))}
                </div>
              )}
              <label className="flex items-start gap-2 text-[13px]">
                <input type="checkbox" className="mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span>These leads agreed to receive WhatsApp messages from us (for example they enquired, or opted in). Sending to people who didn't can get the number restricted by WhatsApp.</span>
              </label>
            </section>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" disabled={!preview || preview.will_send === 0 || !consent || submitting} onClick={start}>
            {submitting ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Send className="mr-1.5 size-4" />}
            {preview ? `Start for ${preview.will_send} lead${preview.will_send === 1 ? "" : "s"}` : "Start campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
